import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import {
	AlertTriangle,
	ChevronRight,
	CurrencyDollar,
	Tool02,
} from "@untitledui/icons";
import { type ChangeEvent, useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataRefresh } from "@/hooks/useSignalR";
import axios from "@/lib/axios";
import { FundingBanner } from "@/components/funding/funding-banner";
import { DirectPayoutCard } from "@/components/direct-payout-card";
import { DataRefreshEvent, type DashboardDto } from "@fp/admin-api-client";
import { useGetApiOnboardingProgress } from "@fp/admin-api-client/onboarding-progress/onboarding-progress.client";
import { useGetSettings } from "@fp/admin-api-client/settings/settings.client";
import { OnboardingProgressBar, allMilestones, nonFundingMilestones } from "@/components/onboarding-progress-bar";
import { useGetOrganizationSettings } from "@fp/admin-api-client/organizations/organizations.client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import { useFundingStatus } from "@/hooks/useFundingStatus";

export const Route = createFileRoute("/dashboard")({
	component: RouteComponent,
});

const daysOptions = [
	{ value: 7, label: "Last 7 days" },
	{ value: 30, label: "Last 30 days" },
	{ value: 90, label: "Last 90 days" },
	{ value: 0, label: "All time" },
];

interface DashboardContentProps {
	data: DashboardDto;
	days: number;
	onDaysChange: (days: number) => void;
}

function DashboardContent({ data, days, onDaysChange }: DashboardContentProps) {
	const navigate = useNavigate();
	const { data: onboardingProgressResponse } = useGetApiOnboardingProgress();
	const { data: settingsResponse } = useGetSettings();
	const { data: orgSettingsResponse } = useGetOrganizationSettings();
	const { data: capabilitiesResponse } = useGetOrganizationContextCapabilities();
	const showDirectPayoutOnHome = orgSettingsResponse?.data?.showDirectPayoutOnHome ?? true;
	const canViewFunding = capabilitiesResponse?.data?.canViewFunding ?? false;

	const handlePendingPayClick = () => {
		navigate({
			to: "/payout-requests/categories/$category",
			params: { category: "review" },
			state: { returnTo: "/dashboard" },
		});
	};

	const handleDaysChange = (e: ChangeEvent<HTMLSelectElement>) => {
		onDaysChange(Number(e.target.value));
	};

	const onboardingProgress = onboardingProgressResponse?.data;
	const onboardingState = settingsResponse?.data?.onboardingProgressState ?? "Expanded";

	return (
		<div className="space-y-6 p-5">
			{onboardingProgress && onboardingState !== "Dismissed" && (
				<OnboardingProgressBar progress={onboardingProgress} steps={canViewFunding ? allMilestones : nonFundingMilestones} defaultCollapsed={onboardingState === "Collapsed"} />
			)}

			{/* Pending Pay */}
			{data.pendingPayCount > 0 && (
				<button
					type="button"
					className="flex rounded-lg px-4 py-3 justify-between w-full text-left cursor-pointer bg-warning-50 border border-warning-300"
					onClick={handlePendingPayClick}
				>
					<div className="font-semibold">
						<div className="flex items-center mb-1">
							<AlertTriangle className="text-warning-600 inline size-5 mr-2" />
							<span className="text-warning-600">Pending Pay</span>
						</div>
						<div className="ml-7 text-sm font-normal text-gray-700">
							Team Members waiting to be paid
						</div>
					</div>
					<div className="flex items-center space-x-3 cursor-pointer">
						<div>{data.pendingPayCount}</div>
						<ChevronRight />
					</div>
				</button>
			)}

			{showDirectPayoutOnHome && <DirectPayoutCard />}

			{/* Top Earners */}
			{data.topEarners.length > 0 && (
				<div>
					<div className="flex items-center justify-between mb-2">
						<div className="flex items-center">
							<span className="text-lg mr-2">
								<CurrencyDollar />
							</span>
							<h3 className="text-lg font-semibold text-gray-900">
								Top Earners
							</h3>
						</div>
						<select className="text-sm text-gray-600 bg-transparent border-none" value={days} onChange={handleDaysChange}>
							{daysOptions.map((opt) => (
								<option key={opt.value} value={opt.value}>{opt.label}</option>
							))}
						</select>
					</div>
					<div className="space-y-3 bg-white border border-gray-200 rounded-lg p-4">
						{data.topEarners.map((earner, index) => (
							<div
								key={`earner-${earner.name}-${index}`}
								className="flex justify-between items-center"
							>
								<span className="text-brand-600 font-medium">
									{earner.name}
								</span>
								<span className="text-gray-900">
									${earner.earnings.toLocaleString()}
								</span>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Top Claimers */}
			{data.topClaimers.length > 0 && (
				<div>
					<div className="flex items-center justify-between mb-2">
						<div className="flex items-center">
							<span className="text-lg mr-2">
								<Tool02 />
							</span>
							<h3 className="text-lg font-semibold text-gray-900">
								Top Claimers
							</h3>
						</div>
						<select className="text-sm text-gray-600 bg-transparent border-none" value={days} onChange={handleDaysChange}>
							{daysOptions.map((opt) => (
								<option key={opt.value} value={opt.value}>{opt.label}</option>
							))}
						</select>
					</div>
					<div className="space-y-3 bg-white border border-gray-200 rounded-lg p-4">
						{data.topClaimers.map((claimer, index) => (
							<div
								key={`claimed-${claimer.name}-${index}`}
								className="flex justify-between items-center"
							>
								<span className="text-brand-600 font-medium">
									{claimer.name}
								</span>
								<span className="text-gray-900">{claimer.claimCount}</span>
							</div>
						))}
					</div>
				</div>
			)}

		</div>
	);
}

function RouteComponent() {
	const [days, setDays] = useState(30);
	const queryClient = useQueryClient();
	const { data: capabilitiesResponse } = useGetOrganizationContextCapabilities();
	const canViewFunding = capabilitiesResponse?.data?.canViewFunding ?? false;
	const { data: fundingStatus } = useFundingStatus({ enabled: canViewFunding });

	const { data, isLoading } = useQuery({
		queryKey: ["dashboard", days],
		queryFn: async () => {
			const response = await axios.get<DashboardDto>(`/api/dashboard?days=${days}`);
			return response.data;
		},
	});

	const invalidateFundingStatus = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
	}, [queryClient]);

	const invalidateDashboard = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["dashboard"] });
		invalidateFundingStatus();
	}, [queryClient, invalidateFundingStatus]);

	useDataRefresh(DataRefreshEvent.PayoutRequestSubmitted, invalidateDashboard);
	useDataRefresh(DataRefreshEvent.PayoutRequestGenerated, invalidateDashboard);
	useDataRefresh(DataRefreshEvent.PayoutRequestAllocated, invalidateDashboard);
	useDataRefresh(DataRefreshEvent.PayoutRequestApproved, invalidateDashboard);
	useDataRefresh(DataRefreshEvent.PayoutRequestDenied, invalidateDashboard);
	useDataRefresh(DataRefreshEvent.PayoutRequestPaid, invalidateDashboard);
	useDataRefresh(DataRefreshEvent.OpportunityClaimed, invalidateDashboard);
	useDataRefresh(DataRefreshEvent.OrganizationFundingCompleted, invalidateFundingStatus);
	useDataRefresh(DataRefreshEvent.OrganizationFundingFailed, invalidateFundingStatus);
	useDataRefresh(DataRefreshEvent.OrganizationBankAccountLinked, invalidateFundingStatus);
	useDataRefresh(DataRefreshEvent.OrganizationBankAccountRemoved, invalidateFundingStatus);

	if (isLoading || !data) {
		return (
			<AdminLayout>
				<AdminPageHeader title="Home" />
				<PageContentContainer className="pt-0 overflow-x-hidden">
					<PageLoading />
				</PageContentContainer>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<AdminPageHeader title="Home" />
			<PageContentContainer className="pt-0 overflow-x-hidden">
				{fundingStatus?.hasLinkedAccount && (
					<div className="p-4">
						<FundingBanner projectedBalance={fundingStatus.projectedBalance} pendingDeposits={fundingStatus.pendingDeposits} lowBalanceThreshold={fundingStatus.lowBalanceThreshold} lowBalanceWarningWarranted={fundingStatus.lowBalanceWarningWarranted} targetBalance={fundingStatus.targetBalance} />
					</div>
				)}
				<DashboardContent data={data} days={days} onDaysChange={setDays} />
			</PageContentContainer>
		</AdminLayout>
	);
}